import { useEffect, useMemo, useState } from "react";

const getStandalone = () => {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(display-mode: standalone)")?.matches) return true;
    if (window.navigator?.standalone) return true;
  } catch (_) {
    return false;
  }
  return false;
};

const getIosSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  return isIOS && isSafari;
};

export const usePwaInstall = () => {
  const [promptEvent, setPromptEvent] = useState(null);
  const [installed, setInstalled] = useState(() => getStandalone());
  const [busy, setBusy] = useState(false);
  const isIosSafari = useMemo(() => getIosSafari(), []);

  useEffect(() => {
    const register = async () => {
      if (!("serviceWorker" in navigator)) return;
      try {
        const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
        registration.update?.();
      } catch (_) {
        /* service worker is optional */
      }
    };

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setPromptEvent(event);
      setInstalled(getStandalone());
    };

    const onInstalled = () => {
      setInstalled(true);
      setPromptEvent(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("load", register, { once: true });
    register();

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("load", register);
    };
  }, []);

  const install = async () => {
    if (installed || busy) return "idle";
    if (isIosSafari) return "ios";
    if (!promptEvent) return "unavailable";
    setBusy(true);
    try {
      promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      setPromptEvent(null);
      if (choice?.outcome === "accepted") {
        setInstalled(true);
        return "accepted";
      }
      return "dismissed";
    } catch (_) {
      return "error";
    } finally {
      setBusy(false);
    }
  };

  return {
    busy,
    canInstall: Boolean(promptEvent) || isIosSafari,
    installed,
    isIosSafari,
    install,
  };
};
